import { LinkOutlined } from '@ant-design/icons'
import { useDriverSchedules } from '@api/useDriverSchedules'
import { useOrders } from '@api/useOrders'
import { GraviButton, Horizontal, NothingMessage, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Col, Radio, Row, Select, Spin } from 'antd'
import moment from 'moment'
import { useEffect, useState } from 'react'

import { StopCard } from '../../OrderWizard/Panes/DisplayPane/DisplayComponents/StopCards'
import { useAcceptedOrdersContext } from '../Contexts/AcceptedOrdersContext'
import { useScheduleContext } from '../Contexts/ScheduleContext'
import { DispatchToolbar } from './DispatchToolbar'
import { LinkWizardHeaderControls } from './LinkWizard/LinkWizardHeader'
import { Section, StartingLocationInputs } from './LinkWizardComponents'
import { NamePlate } from './NamePlate'
import { TripGrid } from './TripGrid'
import { TripGridCell } from './TripGridCell/TripGridCell'

const { Option } = Select

export function OrderLinkWizard({ linkSelectedSchedule, closeSlideUp, config }) {
  const { setUpLinkSchedules, schedules, loadingSchedule } = useScheduleContext()
  const { schedule, tripIndex } = linkSelectedSchedule

  const [secondLegStopIndex, setSecondLegStopIndex] = useState(1)
  const [currentStep, setCurrentStep] = useState(1)
  const [reasonCode, setReasonCode] = useState()
  const [location, setLocation] = useState()

  const { useOrderByIdQuery } = useOrders()
  const { data: orderData } = useOrderByIdQuery(schedule.trips[tripIndex].order_id)
  const showLoading = loadingSchedule || !orderData
  const showNoApplicable = !showLoading && schedules?.length === 0
  const showFirstStep = orderData && !showNoApplicable && currentStep === 1
  const showSecondStep = orderData && !showNoApplicable && currentStep === 2

  useEffect(() => {
    setUpLinkSchedules(schedule)
    if (orderData) {
      setSecondLegStopIndex(orderData.loads.length)
    }
  }, [linkSelectedSchedule, orderData])
  return (
    <Vertical>
      <LinkWizardHeaderControls closeSlideUp={closeSlideUp} />
      <LinkWizardHeader linkSelectedSchedule={linkSelectedSchedule} />
      <Vertical
        border='all'
        bg='default'
        horizontalCenter
        flex={1}
        style={{ padding: '1rem 4rem', overflowY: 'scroll' }}
      >
        {loadingSchedule && <Spin />}
        {showNoApplicable && (
          <NothingMessage
            title='No Applicable Schedules'
            message='There are no applicable driver shifts to split this order with. Please add a shift for this depot on the next shift. '
          />
        )}
        {showFirstStep && (
          <Vertical gap={32} style={{ width: '800px', overflow: undefined }}>
            <Section title='Why is the order being linked?'>
              <Select
                style={{ width: '100%' }}
                value={reasonCode}
                onChange={(value) => setReasonCode(value)}
                placeholder='Select Reason'
              >
                <Option value='Preload - Favorable Market'>Preload - Favorable Market</Option>
                <Option value='Preload - Driver Capacity Constraints'>Preload - Driver Capacity Constraints</Option>
                <Option value='Preload - Other'>Preload - Other</Option>
                <Option value='Retain - Driver Capacity Constraint'>Retain - Driver Capacity Constraint </Option>
                <Option value='Retain - Other'>Retain - Other</Option>
                <Option value='Sleeper Load'>Sleeper Load</Option>
              </Select>
            </Section>
            <Section title='Starting Location (Second Leg)'>
              <StartingLocationInputs value={location} onChange={setLocation} />
            </Section>
            <StepSelect
              setSecondLegStopIndex={setSecondLegStopIndex}
              secondLegStopIndex={secondLegStopIndex}
              trip={orderData}
            />
            <NavigationButtons setCurrentStep={setCurrentStep} disabled={!reasonCode || !location} />
          </Vertical>
        )}
        {showSecondStep && (
          <Vertical gap={32} style={{ width: '1200px' }}>
            <Section title='First Leg Driver'>
              <FirstLeg schedule={schedule} tripIndex={tripIndex} config={config} />
            </Section>
            <div style={{ flexGrow: 1, display: 'flex' }}>
              <Section title='Select Second Leg Driver'>
                <SecondLeg
                  location={location}
                  reasonCode={reasonCode}
                  closeSlideUp={closeSlideUp}
                  linkSelectedSchedule={linkSelectedSchedule}
                  secondLegStopIndex={secondLegStopIndex}
                  config={config}
                />
              </Section>
            </div>
          </Vertical>
        )}
      </Vertical>
    </Vertical>
  )
}
function LinkWizardHeader({ linkSelectedSchedule }) {
  const { schedule, tripIndex } = linkSelectedSchedule
  const tripToSplit = schedule.trips[tripIndex]
  return (
    <Horizontal
      className='link-wizard-header'
      border='all'
      gap={16}
      verticalCenter
      flex='0'
      style={{ width: '100%', overflow: 'visible' }}
    >
      <Col className='vertical-flex-center '>
        <LinkOutlined style={{ fontSize: '3em' }} />
      </Col>
      <Col flex={1}>
        <Texto category='h2' className='link-wizard-title mb-1'>
          Order Link Wizard
        </Texto>
        <Texto category='p'>Split an order between two drivers to ensure completion.</Texto>
      </Col>
      <Col className='split-detail pr-5'>
        <Texto category='p1'>ORDER TO SPLIT</Texto>
        <Texto category='h3'>Order No. {tripToSplit.number}</Texto>
        <Texto category='h5'>{tripToSplit.loads.length + tripToSplit.drops.length} Stops</Texto>
      </Col>
      <Col span={4}>
        <Texto category='p1'>ON SCHEDULE FOR</Texto>
        <Texto category='h3'>{schedule.driver_name}</Texto>
        <Texto category='h5'>{formatters.DATE(moment(schedule.shift_start))}</Texto>
      </Col>
    </Horizontal>
  )
}

function FirstLeg({ schedule, tripIndex, config }) {
  return (
    <div className='flex first-leg' style={{ borderBottom: 'solid 1px var(--gray-300)', minHeight: '100px' }}>
      <div className='flex-1'>
        <NamePlate schedule={schedule} />
      </div>
      <div className='flex-1 vertical-flex-center items-center' style={{ background: 'var(--gray-100)' }}>
        <i style={{ color: 'var(--gray-600)' }}> Three Other Orders</i>
      </div>
      <div className='flex-1'>
        <TripGridCell trip={schedule.trips[tripIndex]} tripIndex={tripIndex} schedule={schedule} config={config} />
      </div>
    </div>
  )
}
function SecondLeg({ location, linkSelectedSchedule, closeSlideUp, reasonCode, secondLegStopIndex, config }) {
  const { schedule, tripIndex } = linkSelectedSchedule
  const { linkOrderMutation } = useDriverSchedules()
  const { schedulePayload } = useScheduleContext()
  const { metadata } = useAcceptedOrdersContext()
  const queryClient = useQueryClient()
  const linkOrder = async (secondLegSchedule) => {
    const request = {
      src_schedule_id: schedule.id,
      src_version: schedule.version,
      tgt_schedule_id: secondLegSchedule.id,
      tgt_version: secondLegSchedule.version,
      order_id: schedule.trips[tripIndex].order_id,
      link_reason: reasonCode,
      handoff_location_id: location,
      stop_index: secondLegStopIndex,
    }
    await linkOrderMutation.mutateAsync(request)
    queryClient.invalidateQueries({
      queryKey: ['getDriverScheduleQuery', schedulePayload],
    })
    closeSlideUp()
  }
  return (
    <Vertical style={{ overflow: 'auto', flex: 1, flexGrow: 1 }}>
      <DispatchToolbar
        isReadonly
        depots={metadata?.depots ?? []}
        carrierDepots={metadata?.carrier_depots ?? []}
        config={config}
      />
      <TripGrid linkWizardActive linkAction={linkOrder} config={config} />
    </Vertical>
  )
}
function NavigationButtons({ setCurrentStep, disabled }) {
  return (
    <div style={{ alignSelf: 'flex-end' }} className='flex py-3'>
      <GraviButton
        disabled={disabled}
        onClick={() => setCurrentStep(2)}
        theme2
        size='large'
        style={{ width: 200 }}
        buttonText='Next'
      />
    </div>
  )
}

function StepSelect({ trip, secondLegStopIndex, setSecondLegStopIndex }) {
  return (
    <Section title='First Stop (Second Leg)'>
      <Radio.Group
        className='step-select'
        value={secondLegStopIndex}
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        onChange={(e) => setSecondLegStopIndex(e.target.value)}
        options={[
          ...trip.loads.map((load, i) => ({
            value: i,
            disabled: i === 0,
            className: 'my-class',
            label: (
              <Row>
                <Col flex={1}>
                  <StopCard key={i} type='load' active={i === secondLegStopIndex} disabled={i === 0} stop={load} />
                </Col>
              </Row>
            ),
          })),

          ...trip.drops.map((drop, i) => ({
            value: i + trip.loads.length,
            label: (
              <Row>
                <Col flex={1}>
                  <StopCard
                    key={i + trip.loads.length}
                    type='drop'
                    active={i + trip.loads.length === secondLegStopIndex}
                    stop={drop}
                  />
                </Col>
              </Row>
            ),
          })),
        ]}
      />
    </Section>
  )
}
